import { Controller } from "@hotwired/stimulus"
import { cable } from "../lair/cable.js"
import { chatClock, chatWhen, chatLayout } from "../lair/chat_time.js"

// The chat page between two friends (ChatsController). Lines go up by POST
// and come back over ChatChannel to every open page of both players. A line
// sent here is drawn at once as "Sending…" and becomes the real one when
// either the POST answer or the wire's copy arrives, whichever is first, so
// it is never drawn twice. The wire also carries the friend's "Seen" and
// "typing", and every other friend's lines for the chat list.
const TYPING_EVERY_MS = 3000
const TYPING_SHOWN_MS = 5000
const NEAR_BOTTOM_PX = 96
const COUNT_FROM = 0.8
const INPUT_MAX_PX = 160
const PREVIEW = 60

export default class extends Controller {
  static targets = ["log", "input", "send", "note", "empty", "form", "locked", "jump", "typing", "count", "status", "list"]
  static values = {
    friend: String, friendId: Number, me: Number, limit: Number,
    sendUrl: String, readUrl: String, reportUrl: String, blockUrl: String, friendsUrl: String
  }

  connect() {
    this.seen = new Set([...this.logTarget.querySelectorAll("[data-message-id]")].map((line) => line.dataset.messageId))
    this.arrange()
    this.stampList()
    this.scrollDown()
    this.refreshComposer()
    this.subscription = cable().subscriptions.create("ChatChannel", { received: (data) => this.received(data) })
    this.onVisible = () => {
      if (document.visibilityState !== "visible" || !this.unreadWhileAway) return
      this.unreadWhileAway = false
      this.markRead()
    }
    document.addEventListener("visibilitychange", this.onVisible)
  }

  disconnect() {
    this.subscription?.unsubscribe()
    clearTimeout(this.typingTimer)
    document.removeEventListener("visibilitychange", this.onVisible)
  }

  // ------------------------------------------------------------- the wire

  received(data) {
    if (data?.type === "message") this.receivedMessage(data)
    else if (data?.type === "read" && data.reader_id === this.friendIdValue) this.markSeen()
    else if (data?.type === "typing" && data.sender_id === this.friendIdValue) this.showTyping()
  }

  receivedMessage(message) {
    this.updateList(message)
    const fromFriend = message.sender_id === this.friendIdValue && message.recipient_id === this.meValue
    const fromMe = message.sender_id === this.meValue && message.recipient_id === this.friendIdValue
    if (!fromFriend && !fromMe) return

    if (fromFriend) {
      this.hideTyping()
      this.unlock()
      this.setOn()
    }
    this.append(message)
    if (!fromFriend) return
    if (document.visibilityState === "visible") this.markRead()
    else this.unreadWhileAway = true
  }

  // Someone not yet able to start a chat may answer one: the friend's first
  // line opens the box.
  unlock() {
    if (this.hasLockedTarget) this.lockedTarget.remove()
    if (this.hasFormTarget) this.formTarget.hidden = false
  }

  // ------------------------------------------------------------- sending

  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) this.send(event)
  }

  typed() {
    this.refreshComposer()
    if (!this.inputTarget.value.trim()) return

    const now = Date.now()
    if (now - (this.typingSentAt || 0) < TYPING_EVERY_MS) return
    this.typingSentAt = now
    this.subscription?.perform("typing", { to: this.friendIdValue })
  }

  suggest(event) {
    this.inputTarget.value = event.currentTarget.dataset.text
    this.refreshComposer()
    this.inputTarget.focus()
  }

  async send(event) {
    event.preventDefault()
    const text = this.inputTarget.value.replace(/\s+/g, " ").trim()
    if (!text) return

    const pending = this.draw({ sender_id: this.meValue, text, at: new Date().toISOString() }, { pending: true })
    this.inputTarget.value = ""
    this.typingSentAt = 0
    this.refreshComposer()
    this.say("")
    this.arrange()
    this.scrollDown()
    this.inputTarget.focus()

    try {
      const response = await fetch(this.sendUrlValue, {
        method: "POST", headers: this.headers(), body: JSON.stringify({ text })
      })
      const answer = await response.json().catch(() => ({}))
      if (response.ok) this.settle(pending, answer.message)
      else this.failed(pending, text, answer.error || "That did not send. Try again.")
    } catch {
      this.failed(pending, text, "That did not send. Check your connection and try again.")
    }
  }

  // The POST answered: the pending line becomes the real one, unless the
  // wire's copy already made it so.
  settle(pending, message) {
    if (!message?.id || pending.dataset.messageId === String(message.id)) return
    if (this.seen.has(String(message.id))) {
      pending.remove()
    } else {
      this.adopt(pending, message)
    }
    this.arrange()
  }

  failed(pending, text, reason) {
    pending.remove()
    if (!this.inputTarget.value) this.inputTarget.value = text
    this.refreshComposer()
    this.say(reason)
    this.arrange()
  }

  adopt(line, message) {
    this.seen.add(String(message.id))
    line.dataset.messageId = message.id
    line.dataset.at = message.at
    line.classList.remove("ll-chat__line--pending")
    line.querySelector("time").dateTime = message.at
  }

  refreshComposer() {
    if (!this.hasInputTarget) return

    const input = this.inputTarget
    input.style.height = "auto"
    input.style.height = `${Math.min(input.scrollHeight, INPUT_MAX_PX)}px`
    input.style.overflowY = input.scrollHeight > INPUT_MAX_PX ? "auto" : "hidden"
    this.sendTarget.disabled = !input.value.trim()
    if (this.hasCountTarget) {
      const length = input.value.length
      this.countTarget.hidden = length < this.limitValue * COUNT_FROM
      this.countTarget.textContent = `${length} / ${this.limitValue}`
      this.countTarget.classList.toggle("is-full", length >= this.limitValue)
    }
  }

  // ------------------------------------------------------------- drawing

  append(message) {
    if (!message?.id || this.seen.has(String(message.id))) return

    const mine = message.sender_id === this.meValue
    const stick = mine || this.nearBottom()
    const pending = mine && [...this.logTarget.querySelectorAll(".ll-chat__line--pending")]
      .find((line) => line.querySelector(".ll-chat__bubble").textContent === message.text)
    if (pending) {
      this.adopt(pending, message)
    } else {
      this.seen.add(String(message.id))
      this.draw(message)
    }
    this.arrange()
    if (stick) this.scrollDown()
    else this.jumpTarget.hidden = false
  }

  draw(message, { pending = false } = {}) {
    if (this.hasEmptyTarget) this.emptyTarget.remove()

    const mine = message.sender_id === this.meValue
    const line = el("li", ["ll-chat__line", mine && "ll-chat__line--mine", pending && "ll-chat__line--pending"])
    if (message.id) line.dataset.messageId = message.id
    line.dataset.senderId = message.sender_id
    line.dataset.at = message.at
    line.dataset.read = "false"

    const row = el("div", "ll-chat__row")
    row.append(el("p", "ll-chat__bubble", message.text))
    if (!mine) {
      const report = el("button", "ll-chat__report", "Report")
      report.type = "button"
      report.dataset.action = "chat#report"
      row.append(report)
    }
    const meta = el("p", "ll-chat__meta")
    const time = el("time")
    time.dateTime = message.at
    meta.append(time)
    line.append(row, meta)
    this.logTarget.append(line)
    return line
  }

  // Groups, day names, clocks and "Seen", worked out afresh from the lines.
  arrange() {
    this.logTarget.querySelectorAll(".ll-chat__day").forEach((divider) => divider.remove())
    const lines = [...this.logTarget.querySelectorAll(".ll-chat__line")]
    const now = new Date()
    const layout = chatLayout(lines.map((line) => ({ senderId: Number(line.dataset.senderId), at: new Date(line.dataset.at) })), now)

    lines.forEach((line, index) => {
      const { day, joinedPrev, joinedNext } = layout[index]
      if (day) {
        const divider = el("li", "ll-chat__day", day)
        divider.setAttribute("role", "separator")
        line.before(divider)
      }
      line.classList.toggle("ll-chat__line--joined-prev", joinedPrev)
      line.classList.toggle("ll-chat__line--joined-next", joinedNext)
      line.classList.remove("ll-chat__line--seen")
      line.querySelector(".ll-chat__seen")?.remove()
      const pending = line.classList.contains("ll-chat__line--pending")
      line.querySelector("time").textContent = pending ? "Sending…" : chatClock(new Date(line.dataset.at))
    })

    const lastMine = lines.filter((line) => Number(line.dataset.senderId) === this.meValue && line.dataset.messageId).at(-1)
    if (lastMine?.dataset.read === "true") {
      lastMine.classList.add("ll-chat__line--seen")
      lastMine.querySelector(".ll-chat__meta").append(el("span", "ll-chat__seen", "Seen"))
    }
  }

  markSeen() {
    this.logTarget.querySelectorAll(".ll-chat__line--mine[data-message-id]").forEach((line) => { line.dataset.read = "true" })
    this.arrange()
  }

  // ------------------------------------------------------------- the friend

  showTyping() {
    const stick = this.nearBottom()
    this.typingTarget.hidden = false
    this.setOn()
    clearTimeout(this.typingTimer)
    this.typingTimer = setTimeout(() => this.hideTyping(), TYPING_SHOWN_MS)
    if (stick) this.scrollDown()
  }

  hideTyping() {
    clearTimeout(this.typingTimer)
    if (this.hasTypingTarget) this.typingTarget.hidden = true
  }

  setOn() {
    this.statusTarget.classList.add("is-on")
    this.statusTarget.querySelector("[data-chat-presence]").textContent = "On now"
  }

  // ------------------------------------------------------------- the list

  stampList() {
    const now = new Date()
    this.element.querySelectorAll("[data-chat-when]").forEach((time) => {
      time.textContent = chatWhen(new Date(time.dateTime), now)
    })
  }

  updateList(message) {
    if (!this.hasListTarget) return
    const mine = message.sender_id === this.meValue
    const friendId = mine ? message.recipient_id : message.sender_id
    const row = this.listTarget.querySelector(`[data-chat-row="${friendId}"]`)
    if (!row) return

    const text = message.text.length > PREVIEW ? `${message.text.slice(0, PREVIEW)}…` : message.text
    row.querySelector("[data-chat-preview]").textContent = mine ? `You: ${text}` : text
    let when = row.querySelector("[data-chat-when]")
    if (!when) {
      when = el("time", "ll-chat-list__when")
      when.dataset.chatWhen = ""
      row.querySelector(".ll-chat-list__side").prepend(when)
    }
    when.dateTime = message.at
    when.textContent = chatWhen(new Date(message.at), new Date())
    if (!mine && friendId !== this.friendIdValue) {
      const badge = row.querySelector("[data-chat-unread]")
      badge.textContent = Number(badge.textContent || 0) + 1
      badge.hidden = false
    }
    this.listTarget.prepend(row.closest("li"))
  }

  // ------------------------------------------------------------- safety

  async report(event) {
    const button = event.currentTarget
    const line = button.closest("[data-message-id]")
    if (!line || !window.confirm("Report this message? A warden will read it.")) return

    const response = await fetch(this.reportUrlValue, {
      method: "POST", headers: this.headers(), body: JSON.stringify({ message_id: line.dataset.messageId })
    }).catch(() => null)
    const answer = await response?.json().catch(() => ({}))
    if (response?.ok) {
      button.disabled = true
      button.textContent = "Reported"
      button.classList.add("is-done")
    }
    this.say(answer?.message || answer?.error || "That did not go through. Try again.")
  }

  async block() {
    if (!window.confirm(`Block ${this.friendValue}? You stop being friends, and they can't message you.`)) return

    const response = await fetch(this.blockUrlValue, { method: "POST", headers: this.headers() }).catch(() => null)
    if (response?.ok) {
      window.location.href = this.friendsUrlValue
    } else {
      this.say("That did not go through. Try again.")
    }
  }

  // ------------------------------------------------------------- scrolling

  nearBottom() {
    const log = this.logTarget
    return log.scrollHeight - log.scrollTop - log.clientHeight < NEAR_BOTTOM_PX
  }

  scrolled() {
    if (this.nearBottom()) this.jumpTarget.hidden = true
  }

  jumpDown() {
    this.scrollDown({ smooth: true })
  }

  scrollDown({ smooth = false } = {}) {
    this.logTarget.scrollTo({ top: this.logTarget.scrollHeight, behavior: smooth ? "smooth" : "auto" })
    this.jumpTarget.hidden = true
  }

  // ------------------------------------------------------------- plumbing

  markRead() {
    fetch(this.readUrlValue, { method: "POST", headers: this.headers() }).catch(() => {})
  }

  say(text) {
    this.noteTarget.textContent = text
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
    }
  }
}

function el(tag, className, text) {
  const node = document.createElement(tag)
  const classes = [className].flat().filter(Boolean)
  if (classes.length) node.className = classes.join(" ")
  if (text != null) node.textContent = text
  return node
}
